// Cancer Recommendations Translation File
// All hardcoded English strings from cancerRecommendations.js and healthScreeningRecommendations.js
// with Chinese translations using standard medical terminology.

const T = {
  en: {
    // ─── CANCER LABELS ───
    "cancer.lung": "Lung Cancer",
    "cancer.breast": "Breast Cancer",
    "cancer.cervical": "Cervical Cancer",
    "cancer.ovarian": "Ovarian Cancer",
    "cancer.uterine": "Uterine Cancer",
    "cancer.colorectal": "Bowel Cancer",
    "cancer.liver": "Liver Cancer",
    "cancer.prostate": "Prostate Cancer",
    "cancer.testicular": "Testicular Cancer",
    "cancer.thyroid": "Thyroid Cancer",
    "cancer.gastric": "Gastrointestinal Cancer",
    "cancer.skin": "Skin Cancer",

    // ─── RISK LEVELS ───
    "risk.high": "HIGH RISK",
    "risk.elevated": "ELEVATED RISK",
    "risk.moderate": "MODERATE RISK",
    "risk.low": "LOW RISK",

    // ─── SCREENING GUIDELINES ───
    "screening.lung.method": "Low-dose CT (LDCT)",
    "screening.lung.guideline": "USPSTF Grade B",
    "screening.lung.who": "Adults 50-80 with ≥20 pack-year history who currently smoke or quit within 15 years",
    "screening.lung.interval": "Annually",
    "screening.lung.source": "USPSTF 2021; ACS 2025",

    "screening.breast.method": "Mammography",
    "screening.breast.guideline": "USPSTF Grade B",
    "screening.breast.who": "Women 40-74 (USPSTF); 40+ (ACS 2025 recommends starting at 40)",
    "screening.breast.interval": "Every 1-2 years (USPSTF); annually (ACS)",
    "screening.breast.highRisk": "Women at high risk: annual mammogram + breast MRI starting at 30",
    "screening.breast.source": "USPSTF 2024; ACS 2025",

    "screening.cervical.method": "Pap smear + HPV co-testing",
    "screening.cervical.guideline": "USPSTF Grade A",
    "screening.cervical.who": "Women 21-65",
    "screening.cervical.interval": "Pap every 3 years (21-29); Pap+HPV every 5 years or Pap every 3 years (30-65)",
    "screening.cervical.source": "USPSTF 2018; ACS 2020",

    "screening.colorectal.method": "FIT (stool test), FIT-DNA (Cologuard), or colonoscopy",
    "screening.colorectal.guideline": "USPSTF Grade A (45-75)",
    "screening.colorectal.who": "Adults 45-75 (Grade A); 76-85 selective (Grade C)",
    "screening.colorectal.interval": "Colonoscopy every 10 years, FIT annually, FIT-DNA every 1-3 years",
    "screening.colorectal.source": "USPSTF 2021; ACS 2018",

    "screening.prostate.method": "PSA blood test (discuss with your doctor)",
    "screening.prostate.guideline": "USPSTF Grade C (shared decision-making)",
    "screening.prostate.who": "Men 55-69; earlier for high-risk (African American, family history) starting at 40-45",
    "screening.prostate.interval": "Every 1-2 years if screening",
    "screening.prostate.source": "USPSTF 2018; ACS 2023",

    "screening.liver.method": "Ultrasound ± AFP blood test",
    "screening.liver.guideline": "AASLD recommendation",
    "screening.liver.who": "Adults with cirrhosis, chronic HBV/HCV, or high-risk populations",
    "screening.liver.interval": "Every 6 months",
    "screening.liver.source": "AASLD 2023; ACS",

    "screening.thyroid.method": "Thyroid ultrasound, TSH blood test",
    "screening.thyroid.guideline": "ATA recommendation (no USPSTF screening rec for general pop)",
    "screening.thyroid.who": "Individuals with family history, radiation exposure, or thyroid nodules",
    "screening.thyroid.interval": "As clinically indicated",
    "screening.thyroid.source": "ATA 2015; NCCN",

    "screening.ovarian.method": "CA-125 blood test ± transvaginal ultrasound",
    "screening.ovarian.guideline": "No USPSTF recommendation for general population (Grade D)",
    "screening.ovarian.who": "High-risk women (BRCA1/2, Lynch syndrome, strong family history)",
    "screening.ovarian.interval": "Every 6-12 months for high-risk",
    "screening.ovarian.source": "NCCN 2024; ACS",

    "screening.uterine.method": "Transvaginal ultrasound; biopsy if indicated",
    "screening.uterine.guideline": "ACS recommendation for high-risk",
    "screening.uterine.who": "Women with Lynch syndrome starting at 35; postmenopausal women with abnormal bleeding",
    "screening.uterine.interval": "Annual biopsy for Lynch syndrome",
    "screening.uterine.source": "ACS; NCCN 2024",

    "screening.skin.method": "Monthly self-exam + annual dermatologist check",
    "screening.skin.guideline": "USPSTF Grade I (insufficient evidence for general pop)",
    "screening.skin.who": "Individuals with fair skin, history of sunburns, family history, many moles",
    "screening.skin.interval": "Annual dermatologic exam for high-risk; monthly self-exam for all",
    "screening.skin.source": "AAD; ACS 2024",

    "screening.gastric.method": "H. pylori test (breath/stool); endoscopy if high-risk",
    "screening.gastric.guideline": "No USPSTF recommendation; ACS recommends for high-risk",
    "screening.gastric.who": "High-risk: family history, H. pylori, Asian/Hispanic ethnicity, high-salt diet",
    "screening.gastric.interval": "Every 1-3 years for high-risk",
    "screening.gastric.source": "ACS; NCCN; Asian screening programs",

    "screening.testicular.method": "Testicular self-exam + clinical exam",
    "screening.testicular.guideline": "USPSTF Grade D for screening in general pop, but ACS recommends awareness",
    "screening.testicular.who": "All men, especially 15-35; higher risk with cryptorchidism or family history",
    "screening.testicular.interval": "Monthly self-exam; clinical exam during routine visits",
    "screening.testicular.source": "ACS 2024",

    // ─── AGE DETAILS ───
    "age.detail.lung": "Lung cancer incidence increases significantly after age 50. Most cases are diagnosed in people 65+.",
    "age.detail.breast": "Breast cancer risk increases with age. About 2/3 of invasive breast cancers are found in women 55+.",
    "age.detail.colorectal": "Colorectal cancer risk rises sharply after 45. USPSTF recommends screening starting at age 45.",
    "age.detail.prostate": "Prostate cancer is rare before 40 but incidence increases steeply after 50.",
    "age.detail.cervical": "Cervical cancer is most common in women 35-44, with median diagnosis at age 50.",
    "age.detail.ovarian": "Ovarian cancer risk increases with age, with most cases diagnosed after 50.",
    "age.detail.uterine": "Most uterine cancers occur after menopause, with median diagnosis at 60.",
    "age.detail.liver": "Liver cancer incidence peaks between ages 55-64 (SEER).",
    "age.detail.thyroid": "Thyroid cancer can occur at any age but is most common in the 30-50 age range.",
    "age.detail.skin": "Melanoma risk increases with age and cumulative sun exposure.",
    "age.detail.gastric": "Stomach cancer risk increases significantly after age 50, with most cases diagnosed 65-74.",
    "age.detail.testicular": "Testicular cancer is most common in men aged 15-35.",
    "age.detail.default": "At age {{age}}, screening becomes increasingly important.",

    // ─── RISK FACTOR TEMPLATES ───
    "risk.age.factor": "Age {{age}}",
    "risk.family.factor": "Family history of {{cancer}}",
    "risk.family.detail": "Having a first-degree relative with {{cancer}} increases risk approximately {{mult}}x (NCI SEER).",
    "risk.ethnicity.factor": "Ethnicity ({{ethnicity}})",
    "risk.ethnicity.detail.higher": "{{ethnicity}} populations have higher incidence rates for {{cancer}} (+{{pct}}%, SEER 2017-2021{{extra}}).",
    "risk.ethnicity.detail.lower": "{{ethnicity}} populations have lower incidence rates for {{cancer}} ({{pct}}%, SEER 2017-2021{{extra}}).",
    "risk.ageContext.factor": "Age context",
    "risk.ageContext.skinYoungCaucasian": "While fair skin significantly increases lifetime melanoma risk, this risk grows with age and cumulative sun exposure. At your age, focus on prevention (sunscreen, avoiding burns) rather than urgent screening — unless you notice new or changing moles.",
    "risk.bmi.factor": "BMI {{bmi}} ({{category}})",
    "risk.bmi.detail": "A BMI of {{bmi}} ({{category}}) is associated with a {{pct}}% increased risk for {{cancer}} (WCRF/AICR; Chen et al. 2023 umbrella review of meta-analyses, PMID:37139871; Fontvieille et al. 2023, EPIC+UK Biobank, PMID:37993940).",
    "risk.smoking.factor": "Smoking ({{packYears}} pack-years)",
    "risk.smoking.detail": "{{packYears}} pack-years of smoking increases {{cancer}} risk by approximately {{pct}}% (IARC, NCI; Zhang et al. 2022, UK Biobank 345K participants: heavy smokers HR 4.27 for lung cancer, PMID:35194190; Wang et al. 2024, 1M participants in China: pack-years is top risk factor, PMID:38253312).",
    "risk.secondhandSmoke.factor": "Secondhand smoke exposure",
    "risk.secondhandSmoke.detail": "Long-term secondhand smoke exposure increases {{cancer}} risk by approximately {{pct}}% (EPA, WHO).",
    "risk.secondhand.factor": "Secondhand smoke exposure",
    "risk.secondhand.detail": "Long-term secondhand smoke exposure increases {{cancer}} risk by ~{{pct}}% (EPA, WHO).",
    "risk.alcohol.factor": "Alcohol consumption ({{category}})",
    "risk.alcohol.detail": "{{category}} alcohol intake increases {{cancer}} risk by ~{{pct}}% (WCRF; Jun et al. 2023 meta-analysis of 139 cohort studies, PMID:37905315 — found no safe level of alcohol consumption for cancer risk).",
    "risk.smoking_alcohol.factor": "Smoking + alcohol interaction",
    "risk.smoking_alcohol.detail": "Combined smoking and alcohol use has a multiplicative effect on {{cancer}} risk, adding approximately {{pct}}% additional risk (IARC).",
    "risk.radiation.factor": "Radiation exposure",
    "risk.smokingAlcohol.factor": "Smoking + alcohol interaction",
    "risk.smokingAlcohol.detail": "Combined smoking and alcohol use has a multiplicative effect on {{cancer}} risk, adding approximately {{pct}}% additional risk (IARC; Burton et al. 2024, PMID:38000113).",
    "risk.radiation.factor": "Radiation exposure",
    "risk.radiation.detail": "Radiation exposure increases {{cancer}} risk by ~{{pct}}% (NCI, UNSCEAR).",
    "risk.diet.factor": "Unhealthy dietary habits",
    "risk.chronic.factor": "Chronic condition: {{condition}}",
    "risk.chronic.detail": "Having chronic {{condition}} conditions increases {{cancerKey}} cancer risk by ~{{pct}}% (clinical evidence).",
    "risk.diet.factor": "Unhealthy dietary habits",
    "risk.diet.detail": "High sodium, processed meats, and saturated fats increase {{cancer}} risk by ~{{pct}}% (WCRF/AICR).",

    // ─── STATISTICS LABELS ───
    "stats.baseline": "Population baseline lifetime risk",
    "stats.adjusted": "Your estimated adjusted risk",
    "stats.multiplier": "Combined risk multiplier",
    "stats.multiplier.value": "{{value}}x baseline",

    // ─── DESCRIPTION TEMPLATES ───
    "desc.noRisk": "Based on your age ({{age}}), routine {{cancer}} screening is recommended as a preventive measure. Regular screening helps with early detection.",
    "desc.high": "Based on your profile — including {{factors}} — you may have a higher-than-average risk for {{cancer}}. If you have any concerns or unusual symptoms, consider discussing screening options with your doctor at your next visit.",
    "desc.elevated": "Your combination of {{factors}} may place you at a moderately increased risk for {{cancer}}. If you have any concerns or unusual symptoms, consider discussing screening options with your doctor at your next visit.",
    "desc.default": "Based on {{factors}}, you may benefit from {{cancer}} screening. If you have any concerns or unusual symptoms, consider discussing your options with your doctor.",

    // ─── GUIDELINE NOTES ───
    "guideline.cervical.under25": "Guidelines recommend cervical screening from age 25 for women",
    "guideline.cervical.over65": "Guideline-recommended cervical screening applies up to age 65",
    "guideline.breast.under30": "Guidelines recommend breast screening from age 30 for high-risk individuals, or age 50 for average risk",
    "guideline.breast.under50": "Guidelines recommend breast screening from age 50 for average risk, or from age 30 with family history/BRCA",
    "guideline.breast.over74": "Guideline-funded breast screening applies up to age 74",
    "guideline.colorectal.under40": "Guidelines recommend colorectal screening from age 40 for high-risk individuals, or age 50 for average risk",
    "guideline.colorectal.under50": "Guidelines recommend colorectal screening from age 50 for average risk, or from age 40 with family history",
    "guideline.colorectal.over74": "Guideline-recommended colorectal screening applies up to age 74",
    "guideline.lung.under50": "Guidelines recommend lung screening from age 50 with ≥20 pack-years smoking history",
    "guideline.lung.over80": "Guideline-recommended lung screening applies up to age 80",
    "guideline.lung.default": "Guidelines recommend lung screening for ages 50-80 with ≥20 pack-years who currently smoke or quit within 15 years",

    // ─── ACADEMIC REFERENCE FINDINGS ───
    "ref.1.finding": "139 cohort studies: no safe level of alcohol for cancer risk; even light drinking (0.01-12.4g/day) significantly increases esophageal, colorectal, and breast cancer risk.",
    "ref.2.finding": "Each 5 kg/m² BMI increase → 1.01-1.13x risk for multiple cancers. Obesity linked to 13+ cancer types including cervical, thyroid, colorectal, liver.",
    "ref.3.finding": "577,343 adults: per ~5 kg/m², HR 1.11 for obesity-related cancers. Additive interaction between obesity and cardiovascular disease (RERI 0.28).",
    "ref.4.finding": "Obesity, male sex, Caucasian ethnicity, family history, and IBD are independent risk factors for early-onset CRC.",
    "ref.5.finding": "High sedentary behavior increases endometrial cancer risk by 36%, colon cancer by 12%, and lung cancer risk.",
    "ref.6.finding": "345,794 UK Biobank participants: heavy smoking → HR 4.27 for lung cancer; combined high genetic risk + heavy smoking → HR 6.19.",
    "ref.7.finding": "Metabolic syndrome increases GI cancer risk; obesity is independently associated with colorectal, gastric, and liver cancer.",
    "ref.8.finding": "ALDH2-deficient East Asian individuals face significantly higher alcohol-related cancer risk with dose-response curves for liver and esophageal cancers.",
    "ref.9.finding": "1,031,911 participants: 7 risk factors identified for personalized lung cancer screening including pack-years, secondhand smoke, family history.",
    "ref.10.finding": "Black women have higher lung cancer incidence despite lower smoking prevalence, supporting ethnicity-specific risk adjustments.",

    // ─── HEALTH MANUAL — DIET ───
    "manual.diet.bmi.severe.title": "Your BMI is {{bmi}} — Severe Obesity (Class II-III)",
    "manual.diet.bmi.severe.detail": "A BMI ≥35 is associated with significantly elevated cancer risk for 13+ cancer types including breast, colorectal, endometrial, liver, and kidney. Each 5 kg/m² increases overall cancer risk by 11% (Fontvieille et al. 2023, EPIC+UK Biobank). Weight loss of even 5-10% of body weight meaningfully reduces cancer biomarkers.",
    "manual.diet.bmi.severe.evidence": "Fontvieille et al., BMC Med 2023, PMID:37993940; NCI Obesity & Cancer 2022",
    "manual.diet.calorie.title": "Target 500-750 Calorie Daily Deficit for Gradual Loss",
    "manual.diet.calorie.detail": "Aim for 1-2 lbs/week weight loss through sustainable dietary changes — not crash diets. Consider consulting a registered dietitian for a structured plan. Mediterranean and DASH diets have the best evidence for both cancer prevention and weight management.",
    "manual.diet.calorie.evidence": "WCRF/AICR 2018; Dominguez et al., Exp Gerontol 2023, PMID:36792040",
    "manual.diet.bmi.obese.title": "Your BMI is {{bmi}} — Obese (Class I)",
    "manual.diet.bmi.obese.detail": "Obesity increases cancer risk for breast (postmenopausal, +30%), colorectal (+30%), endometrial (+150%), and liver cancer (+80%). An umbrella review of meta-analyses (Chen et al. 2023) confirmed obesity links to 13+ cancer types. Even modest weight reduction helps.",
    "manual.diet.bmi.obese.evidence": "Chen et al., Nutr Cancer 2023, PMID:37139871; WCRF/AICR 2018",
    "manual.diet.bmi.overweight.title": "Your BMI is {{bmi}} — Overweight",
    "manual.diet.bmi.overweight.detail": "Being overweight (BMI 25-29.9) modestly increases risk for several cancers. Preventing further weight gain and aiming for a healthy BMI through balanced eating and regular activity is the goal.",
    "manual.diet.bmi.overweight.evidence": "WCRF/AICR 2018",
    "manual.diet.bmi.underweight.title": "Your BMI is {{bmi}} — Underweight",
    "manual.diet.bmi.underweight.detail": "Underweight status (BMI <18.5) can weaken immune function and is associated with increased esophageal and lung cancer risk. Focus on nutrient-dense foods to reach a healthy weight.",
    "manual.diet.bmi.underweight.evidence": "Chen et al., Nutr Cancer 2023, PMID:37139871",
    "manual.diet.bmi.healthy.title": "Your BMI is {{bmi}} — Healthy Weight ✓",
    "manual.diet.bmi.healthy.detail": "Maintaining a healthy BMI (18.5-24.9) is one of the most impactful things you can do for cancer prevention. Keep it up through balanced nutrition and regular activity.",
    "manual.diet.bmi.healthy.evidence": "WCRF/AICR 2018",
    "manual.diet.unhealthy.title": "Your Diet Needs Attention — High Sodium/Processed Food Intake",
    "manual.diet.unhealthy.detail": "Based on your responses, your diet includes patterns linked to increased cancer risk. High sodium damages stomach lining and promotes gastric cancer. Processed meats are IARC Group 1 carcinogens — each 50g/day increases CRC risk by 18%. Saturated fats promote inflammation.",
    "manual.diet.unhealthy.evidence": "IARC Monograph Vol. 114; WCRF/AICR CUP; Bouvard et al., Lancet Oncol 2015",
    "manual.diet.actionable.title": "Actionable Diet Changes",
    "manual.diet.actionable.detail": "1) Limit red meat to 3 servings/week, avoid processed meats (bacon, sausage, deli). 2) Reduce sodium to <2,300mg/day — read labels, cook from scratch. 3) Replace saturated fats with olive oil, nuts, avocado. 4) Add 25-30g/day fiber through whole grains, beans, vegetables.",
    "manual.diet.actionable.evidence": "ACS Nutrition Guidelines 2024; WCRF/AICR 2018",
    "manual.diet.good.title": "Your Dietary Habits Look Good — Keep Optimizing",
    "manual.diet.good.detail": "You reported no major unhealthy dietary patterns. To further reduce cancer risk: aim for 5+ servings of fruits/vegetables daily (400g+), include cruciferous vegetables (broccoli, kale — contain sulforaphane), and choose whole grains over refined grains.",
    "manual.diet.good.evidence": "Aune et al., Int J Epidemiol 2017, PMID:28338764 (142 publications)",

    // ─── HEALTH MANUAL — LIFESTYLE ───
    "manual.lifestyle.active_smoker.title": "Active Smoker — {{packYears}} Pack-Years — This Is Your #1 Priority",
    "manual.lifestyle.active_smoker.detail": "Tobacco is the single largest preventable cause of cancer death. At {{packYears}} pack-years, your lung cancer risk is significantly elevated (HR {{hr}} vs. never-smokers, Zhang et al. 2022 UK Biobank). Quitting at any age provides benefit — lung cancer risk drops ~50% within 10 years of quitting.",
    "manual.lifestyle.active_smoker.detail_light": "Any amount of smoking increases cancer risk. At {{packYears}} pack-years, your exposure is still relatively low (HR {{hr}} vs. never-smokers), but quitting now means your risk can return close to baseline within a few years. The best time to quit is today.",
    "manual.lifestyle.active_smoker.evidence": "Zhang et al., Br J Cancer 2022, PMID:35194190; US Surgeon General 2014",
    "manual.lifestyle.cessation.title": "Evidence-Based Cessation Options",
    "manual.lifestyle.cessation.detail": "Combination therapy has highest success rate: 1) Nicotine replacement (patch + gum/lozenge), 2) Varenicline (Chantix) — most effective single medication, 3) Bupropion (Wellbutrin). Combining medication with counseling doubles quit rates. Free help: 1-800-QUIT-NOW or smokefree.gov.",
    "manual.lifestyle.cessation.evidence": "CDC Clinical Practice Guideline for Treating Tobacco Use 2020; Cahill et al., Cochrane 2016",
    "manual.lifestyle.former_smoker.title": "Former Smoker ({{packYears}} Pack-Years) — Your Risk Is Decreasing",
    "manual.lifestyle.former_smoker.detail": "Excellent decision to quit. Your cancer risk is actively declining. After 5 years, risk of mouth/throat/esophageal/bladder cancer drops by half. After 10 years, lung cancer risk drops ~50%. After 15 years, coronary heart disease risk approaches never-smokers.",
    "manual.lifestyle.former_smoker.evidence": "US Surgeon General 2020; ACS",
    "manual.lifestyle.ldct_eligible.title": "You May Qualify for Lung Cancer Screening (LDCT)",
    "manual.lifestyle.ldct_eligible.detail": "With {{packYears}} pack-years, you may qualify for annual low-dose CT screening if you quit within the last 15 years and are 50-80 years old. LDCT reduces lung cancer mortality by 20%.",
    "manual.lifestyle.ldct_eligible.evidence": "USPSTF 2021 (Grade B); National Lung Screening Trial",
    "manual.lifestyle.secondhand.title": "Secondhand Smoke Exposure — Take Protective Action",
    "manual.lifestyle.secondhand.detail": "Living with a smoker increases your lung cancer risk by 20-30%. Secondhand smoke causes ~7,300 lung cancer deaths annually in US non-smokers. Make your home smoke-free and support your household member's quit attempt.",
    "manual.lifestyle.secondhand.evidence": "EPA 1992; US Surgeon General 2006; Wang et al., Chest 2024, PMID:38253312",
    "manual.lifestyle.heavy_alcohol.title": "High Alcohol Consumption — Significant Cancer Risk Factor",
    "manual.lifestyle.heavy_alcohol.detail": "Your reported alcohol intake is in the {{category}} category. A 2023 meta-analysis of 139 cohort studies found heavy drinking (≥50g/day) significantly increases risk for nearly all cancer types. Even light drinking increases breast, colorectal, and esophageal cancer risk. There is no safe level of alcohol for cancer prevention.",
    "manual.lifestyle.heavy_alcohol.detail.asian": " Note: East Asian populations with ALDH2 deficiency (common variant) face even higher alcohol-cancer risk.",
    "manual.lifestyle.heavy_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315; IARC Group 1 carcinogen",
    "manual.lifestyle.alcohol_reduction.title": "Alcohol Reduction Strategy",
    "manual.lifestyle.alcohol_reduction.detail": "Start with alcohol-free days (aim for 3-4/week). Set a drink limit before social events. Switch to lower-alcohol options. Track consumption in a journal. If you're unable to reduce on your own, your doctor can help — medications like naltrexone reduce cravings.",
    "manual.lifestyle.alcohol_reduction.evidence": "ACS Alcohol & Cancer Guidelines; NIAAA",
    "manual.lifestyle.moderate_alcohol.title": "Moderate Alcohol — Consider Reducing for Cancer Prevention",
    "manual.lifestyle.moderate_alcohol.detail": "Even moderate drinking (1-2 drinks/day) increases breast cancer risk by ~10% and colorectal cancer risk by ~10%. The ACS recommends limiting to ≤1 drink/day for women, ≤2 for men — but acknowledges lower is better for cancer specifically.",
    "manual.lifestyle.moderate_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315; ACS Guidelines",
    "manual.lifestyle.light_alcohol.title": "Light Alcohol Use — Low but Non-Zero Cancer Risk",
    "manual.lifestyle.light_alcohol.detail": "Even light drinking slightly increases risk for breast and esophageal cancer. Your current intake is within commonly accepted limits, but be aware that 'no safe level' applies for cancer specifically.",
    "manual.lifestyle.light_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315",
    "manual.lifestyle.none.title": "No Major Lifestyle Risk Factors Detected ✓",
    "manual.lifestyle.none.detail": "You don't smoke and your alcohol consumption is none/minimal. These are two of the most important cancer prevention factors. Maintain these habits — they're protecting you more than you realize.",
    "manual.lifestyle.none.evidence": "ACS Cancer Prevention Guidelines 2024",

    // ─── HEALTH MANUAL — EXERCISE ───
    "manual.exercise.obese.title": "Start at 150 min/week, Progress to 300 min/week",
    "manual.exercise.obese.detail": "Given your BMI, the cancer prevention benefit of exercise is especially high. Physical activity reduces colon cancer risk by 20-25%, breast cancer by 20%, and endometrial cancer by 20%. For weight management, the ACS recommends progressing toward 300 min/week of moderate activity. Start where you're comfortable and build up gradually.",
    "manual.exercise.obese.evidence": "WCRF/AICR 2018; Moore et al., JAMA Intern Med 2016, PMID:27183032 (1.44M participants)",
    "manual.exercise.normal.title": "Aim for 150+ Minutes of Moderate Activity Per Week",
    "manual.exercise.normal.detail": "Regular physical activity is one of the strongest modifiable cancer prevention factors. It reduces risk for colon (20-25%), breast (20%), endometrial (20%), and several other cancers through improved insulin sensitivity, reduced inflammation, and immune regulation.",
    "manual.exercise.normal.evidence": "WCRF/AICR 2018 (convincing evidence); Moore et al., JAMA Intern Med 2016, PMID:27183032",
    "manual.exercise.strength.title": "Add Strength Training 2+ Days Per Week",
    "manual.exercise.strength.detail": "Resistance exercise improves insulin sensitivity, reduces inflammation markers (IL-6, TNF-α), and maintains lean body mass — all cancer-protective mechanisms. A meta-analysis found muscle-strengthening activities associated with 13% lower cancer mortality.",
    "manual.exercise.strength.detail.cardio": " Note: With your cardiovascular history, start with light resistance and consult your doctor on safe intensity levels.",
    "manual.exercise.strength.evidence": "Momma et al., Br J Sports Med 2022, PMID:35228201; WHO 2020",
    "manual.exercise.sitting.title": "Break Up Prolonged Sitting Every 30-60 Minutes",
    "manual.exercise.sitting.detail": "Sedentary behavior (>8 hrs/day) is an independent cancer risk factor. Sitting time increases endometrial cancer risk by 36% and colon cancer by 12%, even in people who exercise regularly (Hermelink et al. 2022). Set hourly movement reminders.",
    "manual.exercise.sitting.evidence": "Hermelink et al., Eur J Epidemiol 2022, PMID:35612669",

    // ─── HEALTH MANUAL — VITAMINS ───
    "manual.vitamins.vitd.title": "Vitamin D — Get Tested, Then Decide on Supplementation",
    "manual.vitamins.vitd.detail": "Low vitamin D (<20 ng/mL) is associated with increased colorectal, breast, and prostate cancer risk. The VITAL trial (n=25,871) found vitamin D supplementation reduced cancer mortality by 25% in longer follow-up analyses. Ask your doctor for a 25(OH)D blood test.",
    "manual.vitamins.vitd.evidence": "Manson et al., NEJM 2019;380:33-44 (VITAL trial); IOM 2011",
    "manual.vitamins.calcium.title": "Calcium 1,000-1,200mg/day for Bone Health",
    "manual.vitamins.calcium.detail": "Adequate calcium is important for bone health after 50. Food sources (dairy, fortified foods, leafy greens) are preferred over supplements. Excessive supplementation (>1,500mg/day) may increase cardiovascular risk.",
    "manual.vitamins.calcium.evidence": "IOM 2011; USPSTF 2018",
    "manual.vitamins.folate.title": "Folate & Iron — Essential for Reproductive-Age Women",
    "manual.vitamins.folate.detail": "Folic acid (400-800 mcg/day) is recommended for all women of childbearing age to prevent neural tube defects. If your periods are heavy or you eat little meat, get your ferritin checked for iron deficiency. Get nutrients from food first.",
    "manual.vitamins.folate.evidence": "USPSTF 2023 (Grade A for folic acid); WHO Iron Guidelines",
    "manual.vitamins.antioxidant.title": "Skip Antioxidant Supplements for Cancer Prevention",
    "manual.vitamins.antioxidant.detail": "Multiple large trials show antioxidant supplements (beta-carotene, vitamin E, selenium) do NOT reduce cancer risk and may INCREASE it. The SELECT trial found vitamin E increased prostate cancer by 17%. Beta-carotene increased lung cancer by 18% in smokers. Get nutrients from whole foods instead.",
    "manual.vitamins.antioxidant.evidence": "Klein et al., JAMA 2011 (SELECT); ATBC Study, NEJM 1994; USPSTF 2022 (Grade I)",

    // ─── HEALTH MANUAL — SCREENING ───
    "manual.screening.colorectal.title": "Colorectal Cancer Screening — Due Now",
    "manual.screening.colorectal.detail.age": "At age {{age}}, you should be actively screening for colorectal cancer (USPSTF Grade A for 45-75).",
    "manual.screening.colorectal.detail.family": "With your family history of colorectal cancer, screening should begin at age 40 or 10 years before your relative's diagnosis age.",
    "manual.screening.colorectal.detail.options": " Options: colonoscopy every 10 years, annual FIT (stool test), or FIT-DNA every 1-3 years.",
    "manual.screening.colorectal.evidence": "USPSTF 2021 (Grade A, 45-75)",
    "manual.screening.breast.title": "Breast Cancer Screening — Mammography Recommended",
    "manual.screening.breast.detail": "At age {{age}}, the USPSTF recommends biennial mammography (updated 2024 to start at 40, was previously 50).",
    "manual.screening.breast.detail.family": " With your family history, discuss whether annual mammography or adding breast MRI is appropriate.",
    "manual.screening.breast.detail.nofamily": " The ACS recommends annual mammography.",
    "manual.screening.breast.evidence": "USPSTF 2024 (Grade B, biennial 40-74); ACS 2015",
    "manual.screening.cervical.title": "Cervical Cancer Screening — Pap + HPV Testing",
    "manual.screening.cervical.detail": "Ages 21-29: Pap smear every 3 years. Ages 30-65: Pap + HPV co-testing every 5 years (preferred).",
    "manual.screening.cervical.detail.hpv": " HPV vaccination is still available for you (approved up to age 45) and prevents ~90% of HPV-related cancers.",
    "manual.screening.cervical.evidence": "USPSTF 2018 (Grade A); CDC HPV Vaccination 2024",
    "manual.screening.prostate.title": "Prostate Cancer — Early Discussion Recommended",
    "manual.screening.prostate.detail.african": "As a Black man, you have 1.7x higher prostate cancer incidence. The PCF recommends screening discussions starting at age 40.",
    "manual.screening.prostate.detail.family": "With your family history, prostate screening discussions should start earlier than the general recommendation of 55.",
    "manual.screening.prostate.detail.suffix": " PSA testing is a shared decision — discuss benefits and risks with your doctor.",
    "manual.screening.prostate.evidence": "USPSTF 2018 (Grade C, 55-69); PCF Black Men Guidelines 2023",
    "manual.screening.liver.title": "Hepatitis B/C Screening — Important for Liver Cancer Prevention",
    "manual.screening.liver.detail.asian": "As someone of Asian descent, you're at higher risk for hepatitis B (higher HBV prevalence in Asian populations). ",
    "manual.screening.liver.detail": "Chronic HBV/HCV causes ~80% of liver cancer. Get tested for both — HCV is now curable in >95% of cases. HBV vaccination is available if you're not immune.",
    "manual.screening.liver.evidence": "USPSTF HCV 2020 (Grade B); AASLD 2023",
    "manual.screening.age_appropriate.title": "Age-Appropriate Screenings to Watch For",
    "manual.screening.age_appropriate.detail": "At age {{age}}, keep these milestones in mind: Cervical screening from 21 (women), colorectal from 45, breast mammography from 40 (women), and prostate discussion from 55 (men, earlier if Black or family history). Stay up to date with your primary care visits.",
    "manual.screening.age_appropriate.evidence": "USPSTF A/B Recommendations 2024",

    // ─── HEALTH MANUAL — PREVENTION ───
    "manual.prevention.diabetes.title": "Diabetes Management Is Cancer Prevention",
    "manual.prevention.diabetes.detail": "Type 2 diabetes increases risk of colorectal (+30%), liver (+100%), pancreatic, breast, and bladder cancer (Zhan et al. 2024). Optimal blood sugar control (HbA1c <7%) reduces these risks. Metformin may have additional cancer-protective effects (under investigation).",
    "manual.prevention.diabetes.evidence": "Zhan et al., J Gastroenterol Hepatol 2024, PMID:38230882; NCI",
    "manual.prevention.cvd.title": "Cardiovascular Disease + Cancer Share Risk Factors",
    "manual.prevention.cvd.detail": "CVD and cancer share common risk factors: obesity, inactivity, smoking, poor diet, inflammation. A 2023 EPIC/UK Biobank study (577K adults) found an additive interaction between obesity and CVD for cancer risk. Managing your CVD risk factors simultaneously reduces your cancer risk.",
    "manual.prevention.cvd.evidence": "Fontvieille et al., BMC Med 2023, PMID:37993940",
    "manual.prevention.sleep.title": "Prioritize 7-9 Hours of Consistent Sleep",
    "manual.prevention.sleep.detail": "The IARC classified night shift work/circadian disruption as a probable carcinogen (Group 2A). Short sleep suppresses melatonin (which has anti-cancer properties) and impairs DNA repair. Maintain consistent sleep/wake times even on weekends.",
    "manual.prevention.sleep.evidence": "IARC Monograph Vol. 124 (2019); Rivera et al., Int J Cancer 2020",
    "manual.prevention.uv.title": "UV Protection — SPF 30+ Daily on Exposed Skin",
    "manual.prevention.uv.detail": "Use broad-spectrum SPF 30+ sunscreen and reapply every 2 hours outdoors. Avoid tanning beds entirely. Melanoma incidence has increased 320% since 1975.",
    "manual.prevention.uv.detail.caucasian": " With Caucasian skin, your melanoma risk is significantly higher — annual dermatologist skin checks are recommended.",
    "manual.prevention.uv.evidence": "AAD; Green et al., J Clin Oncol 2011 (sunscreen reduces melanoma by 50%)",

    // ─── HEALTH SCREENING RECOMMENDATIONS (cardiometabolic) ───
    "hs.bloodSugar.category": "Blood Sugar & Diabetes",
    "hs.cholesterol.category": "Cholesterol",
    "hs.bloodPressure.category": "Blood Pressure",

    "hs.diabetes.diagnosed": "Diagnosed with diabetes",
    "hs.diabetes.prediabetic": "Pre-diabetic blood sugar levels",
    "hs.diabetes.age45": "Age 45+",
    "hs.diabetes.overweight": "Overweight/obese (BMI ≥ 25)",
    "hs.diabetes.ethnicity": "Higher-risk ethnicity (screening recommended at BMI ≥ 23 for Asian populations)",
    "hs.diabetes.family": "Family history of diabetes",
    "hs.diabetes.hypertension": "High blood pressure",
    "hs.diabetes.cholesterol": "High cholesterol",

    "hs.diabetes.screening.method": "Fasting plasma glucose (FPG) or HbA1c blood test",
    "hs.diabetes.screening.guideline": "USPSTF Grade B; ADA Standards of Care 2024",
    "hs.diabetes.screening.who": "Adults 35–70 who are overweight/obese; earlier if risk factors present",
    "hs.diabetes.screening.interval.diagnosed": "HbA1c every 3 months; annual comprehensive metabolic panel",
    "hs.diabetes.screening.interval.high": "Retest every 6–12 months; consider oral glucose tolerance test (OGTT)",
    "hs.diabetes.screening.interval.default": "Every 3 years if normal; annually if risk factors present",
    "hs.diabetes.screening.source": "USPSTF 2021; ADA 2024; CDC",

    "hs.diabetes.action.diagnosed.1": "Monitor blood sugar daily or as directed by your doctor",
    "hs.diabetes.action.diagnosed.2": "Maintain HbA1c below 7% (or your doctor's target)",
    "hs.diabetes.action.diagnosed.3": "Annual eye exam, foot exam, and kidney function test",
    "hs.diabetes.action.diagnosed.4": "Heart-healthy diet low in refined carbs and added sugars",
    "hs.diabetes.action.diagnosed.5": "150+ minutes/week of moderate physical activity",
    "hs.diabetes.action.high.1": "Reduce refined carbohydrate and sugar intake",
    "hs.diabetes.action.high.2": "Aim for 5–7% body weight loss if overweight",
    "hs.diabetes.action.high.3": "150+ minutes/week of moderate exercise (brisk walking, cycling)",
    "hs.diabetes.action.high.4": "Ask your doctor about metformin for diabetes prevention",
    "hs.diabetes.action.high.5": "Retest fasting glucose or HbA1c in 6–12 months",
    "hs.diabetes.action.default.1": "Maintain a balanced diet rich in whole grains, vegetables, and lean protein",
    "hs.diabetes.action.default.2": "Stay physically active — 150+ minutes/week of moderate exercise",
    "hs.diabetes.action.default.3": "Know your numbers: get a fasting glucose test at your next checkup",
    "hs.diabetes.action.default.4": "Limit sugary drinks and processed foods",

    "hs.cholesterol.high": "High cholesterol (≥ 240 mg/dL or diagnosed)",
    "hs.cholesterol.borderline": "Borderline high cholesterol (200–239 mg/dL)",
    "hs.cholesterol.age40": "Age 40+",
    "hs.cholesterol.smoking": "Smoking history",
    "hs.cholesterol.diabetes": "Diabetes",
    "hs.cholesterol.hypertension": "High blood pressure",
    "hs.cholesterol.overweight": "Overweight/obese",
    "hs.cholesterol.male45": "Male age 45+",
    "hs.cholesterol.female55": "Female age 55+",

    "hs.cholesterol.screening.method": "Lipid panel blood test (total cholesterol, LDL, HDL, triglycerides)",
    "hs.cholesterol.screening.guideline": "ACC/AHA 2019; USPSTF Grade B (adults 40-75)",
    "hs.cholesterol.screening.who": "Adults 20+ (baseline); regularly for ages 40-75 or with risk factors",
    "hs.cholesterol.screening.interval.high": "Every 6–12 months to monitor treatment response",
    "hs.cholesterol.screening.interval.moderate": "Annually",
    "hs.cholesterol.screening.interval.default": "Every 4–6 years if normal; more often with risk factors",
    "hs.cholesterol.screening.source": "ACC/AHA 2019; USPSTF 2023",

    "hs.cholesterol.action.high.1": "Discuss statin therapy with your doctor if not already on one",
    "hs.cholesterol.action.high.2": "Reduce saturated fat intake (red meat, full-fat dairy, fried foods)",
    "hs.cholesterol.action.high.3": "Increase soluble fiber (oats, beans, fruits, vegetables)",
    "hs.cholesterol.action.high.4": "Exercise at least 150 minutes/week — even walking helps",
    "hs.cholesterol.action.high.5": "Consider omega-3 supplements or fish oil (consult your doctor)",
    "hs.cholesterol.action.moderate.1": "Adopt a heart-healthy diet (Mediterranean or DASH diet)",
    "hs.cholesterol.action.moderate.2": "Increase physical activity — aim for 30 min/day most days",
    "hs.cholesterol.action.moderate.3": "Limit alcohol to moderate levels",
    "hs.cholesterol.action.moderate.4": "Get a follow-up lipid panel in 6–12 months",
    "hs.cholesterol.action.default.1": "Maintain a heart-healthy diet with healthy fats (olive oil, nuts, avocado)",
    "hs.cholesterol.action.default.2": "Stay active — regular exercise raises HDL (good cholesterol)",
    "hs.cholesterol.action.default.3": "Get a baseline lipid panel if you haven't had one",
    "hs.cholesterol.action.default.4": "Limit trans fats and highly processed foods",

    "hs.bp.high": "High blood pressure (≥ 140/90 mmHg or diagnosed)",
    "hs.bp.elevated": "Elevated blood pressure (120–139/80–89 mmHg)",
    "hs.bp.age45": "Age 45+",
    "hs.bp.african": "African American ethnicity (higher prevalence)",
    "hs.bp.overweight": "Overweight/obese",
    "hs.bp.smoking": "Smoking history",
    "hs.bp.diabetes": "Diabetes",
    "hs.bp.cholesterol": "High cholesterol",
    "hs.bp.alcohol": "Moderate-to-heavy alcohol consumption",

    "hs.bp.screening.method": "Blood pressure measurement (sphygmomanometer or automated cuff)",
    "hs.bp.screening.guideline": "AHA/ACC 2023; USPSTF Grade A (adults 18+)",
    "hs.bp.screening.who": "All adults 18+; more frequently with risk factors",
    "hs.bp.screening.interval.high": "Monitor at home regularly; doctor visits every 1–3 months until controlled",
    "hs.bp.screening.interval.moderate": "Check every 3–6 months; consider home monitoring",
    "hs.bp.screening.interval.default": "At least annually at routine checkup",
    "hs.bp.screening.source": "AHA 2023; USPSTF 2021",

    "hs.bp.action.high.1": "Take prescribed medications as directed — don't skip doses",
    "hs.bp.action.high.2": "Reduce sodium intake to < 2,300 mg/day (ideally < 1,500 mg)",
    "hs.bp.action.high.3": "Follow the DASH diet (fruits, vegetables, whole grains, low-fat dairy)",
    "hs.bp.action.high.4": "Exercise regularly — even 30 min/day of brisk walking helps",
    "hs.bp.action.high.5": "Monitor blood pressure at home and keep a log for your doctor",
    "hs.bp.action.high.6": "Manage stress through meditation, deep breathing, or yoga",
    "hs.bp.action.moderate.1": "Reduce sodium intake — avoid processed and restaurant foods",
    "hs.bp.action.moderate.2": "Increase potassium-rich foods (bananas, spinach, sweet potatoes)",
    "hs.bp.action.moderate.3": "Get at least 150 minutes/week of aerobic exercise",
    "hs.bp.action.moderate.4": "Limit alcohol to ≤ 1 drink/day (women) or ≤ 2 drinks/day (men)",
    "hs.bp.action.moderate.5": "Maintain a healthy weight — even 5-10 lbs loss can help",
    "hs.bp.action.default.1": "Keep sodium intake moderate (< 2,300 mg/day)",
    "hs.bp.action.default.2": "Stay active with regular physical activity",
    "hs.bp.action.default.3": "Get your blood pressure checked at your next doctor visit",
    "hs.bp.action.default.4": "Maintain a healthy weight and manage stress",
  },

  zh: {
    // ─── CANCER LABELS ───
    "cancer.lung": "肺癌",
    "cancer.breast": "乳腺癌",
    "cancer.cervical": "宫颈癌",
    "cancer.ovarian": "卵巢癌",
    "cancer.uterine": "子宫癌",
    "cancer.colorectal": "结直肠癌",
    "cancer.liver": "肝癌",
    "cancer.prostate": "前列腺癌",
    "cancer.testicular": "睾丸癌",
    "cancer.thyroid": "甲状腺癌",
    "cancer.gastric": "消化道癌",
    "cancer.skin": "皮肤癌",

    // ─── RISK LEVELS ───
    "risk.high": "高风险",
    "risk.elevated": "风险偏高",
    "risk.moderate": "中等风险",
    "risk.low": "低风险",

    // ─── SCREENING GUIDELINES ───
    "screening.lung.method": "低剂量CT（LDCT）",
    "screening.lung.guideline": "USPSTF B级推荐",
    "screening.lung.who": "50-80岁、吸烟≥20包年且目前仍在吸烟或戒烟不超过15年的成人",
    "screening.lung.interval": "每年一次",
    "screening.lung.source": "USPSTF 2021; ACS 2025",

    "screening.breast.method": "乳腺X线摄影（钼靶）",
    "screening.breast.guideline": "USPSTF B级推荐",
    "screening.breast.who": "40-74岁女性（USPSTF）；40岁以上（ACS 2025建议从40岁开始）",
    "screening.breast.interval": "每1-2年一次（USPSTF）；每年一次（ACS）",
    "screening.breast.highRisk": "高风险女性：从30岁起每年钼靶+乳腺MRI",
    "screening.breast.source": "USPSTF 2024; ACS 2025",

    "screening.cervical.method": "宫颈涂片（Pap）+ HPV联合检测",
    "screening.cervical.guideline": "USPSTF A级推荐",
    "screening.cervical.who": "21-65岁女性",
    "screening.cervical.interval": "21-29岁每3年Pap检查；30-65岁每5年Pap+HPV联合检测或每3年Pap检查",
    "screening.cervical.source": "USPSTF 2018; ACS 2020",

    "screening.colorectal.method": "粪便免疫化学检测（FIT）、FIT-DNA（Cologuard）或结肠镜检查",
    "screening.colorectal.guideline": "USPSTF A级推荐（45-75岁）",
    "screening.colorectal.who": "45-75岁成人（A级）；76-85岁选择性筛查（C级）",
    "screening.colorectal.interval": "结肠镜每10年一次，FIT每年一次，FIT-DNA每1-3年一次",
    "screening.colorectal.source": "USPSTF 2021; ACS 2018",

    "screening.prostate.method": "PSA血液检测（请与医生讨论）",
    "screening.prostate.guideline": "USPSTF C级推荐（共同决策）",
    "screening.prostate.who": "55-69岁男性；高风险人群（非裔美国人、有家族史）可从40-45岁开始",
    "screening.prostate.interval": "如选择筛查，每1-2年一次",
    "screening.prostate.source": "USPSTF 2018; ACS 2023",

    "screening.liver.method": "腹部超声 ± 甲胎蛋白（AFP）血液检测",
    "screening.liver.guideline": "AASLD推荐",
    "screening.liver.who": "肝硬化、慢性乙肝/丙肝患者或高风险人群",
    "screening.liver.interval": "每6个月一次",
    "screening.liver.source": "AASLD 2023; ACS",

    "screening.thyroid.method": "甲状腺超声、TSH血液检测",
    "screening.thyroid.guideline": "ATA推荐（USPSTF未对一般人群作筛查推荐）",
    "screening.thyroid.who": "有家族史、辐射暴露或甲状腺结节的个人",
    "screening.thyroid.interval": "根据临床需要",
    "screening.thyroid.source": "ATA 2015; NCCN",

    "screening.ovarian.method": "CA-125血液检测 ± 经阴道超声",
    "screening.ovarian.guideline": "USPSTF未推荐一般人群筛查（D级）",
    "screening.ovarian.who": "高风险女性（BRCA1/2基因突变、Lynch综合征、强家族史）",
    "screening.ovarian.interval": "高风险人群每6-12个月一次",
    "screening.ovarian.source": "NCCN 2024; ACS",

    "screening.uterine.method": "经阴道超声；必要时活检",
    "screening.uterine.guideline": "ACS高风险人群推荐",
    "screening.uterine.who": "Lynch综合征女性从35岁开始；绝经后异常出血女性",
    "screening.uterine.interval": "Lynch综合征每年活检",
    "screening.uterine.source": "ACS; NCCN 2024",

    "screening.skin.method": "每月自查 + 每年皮肤科医生检查",
    "screening.skin.guideline": "USPSTF I级（一般人群证据不足）",
    "screening.skin.who": "皮肤白皙、有晒伤史、家族史、痣较多的个人",
    "screening.skin.interval": "高风险人群每年皮肤科检查；所有人每月自查",
    "screening.skin.source": "AAD; ACS 2024",

    "screening.gastric.method": "幽门螺杆菌检测（呼气/粪便）；高风险者行胃镜检查",
    "screening.gastric.guideline": "USPSTF未推荐；ACS推荐高风险人群筛查",
    "screening.gastric.who": "高风险人群：家族史、幽门螺杆菌感染、亚裔/西班牙裔、高盐饮食",
    "screening.gastric.interval": "高风险人群每1-3年一次",
    "screening.gastric.source": "ACS; NCCN; 亚洲筛查项目",

    "screening.testicular.method": "睾丸自查 + 临床检查",
    "screening.testicular.guideline": "USPSTF D级（一般人群），但ACS建议提高认识",
    "screening.testicular.who": "所有男性，尤其是15-35岁；隐睾或有家族史者风险更高",
    "screening.testicular.interval": "每月自查；常规就诊时临床检查",
    "screening.testicular.source": "ACS 2024",

    // ─── AGE DETAILS ───
    "age.detail.lung": "肺癌发病率在50岁后显著增加，大多数病例确诊于65岁以上人群。",
    "age.detail.breast": "乳腺癌风险随年龄增长而升高。约三分之二的浸润性乳腺癌发现于55岁以上女性。",
    "age.detail.colorectal": "结直肠癌风险在45岁后急剧上升。USPSTF建议从45岁开始筛查。",
    "age.detail.prostate": "前列腺癌在40岁前少见，但50岁后发病率急剧上升。",
    "age.detail.cervical": "宫颈癌在35-44岁女性中最常见，中位确诊年龄为50岁。",
    "age.detail.ovarian": "卵巢癌风险随年龄增加，大多数病例确诊于50岁之后。",
    "age.detail.uterine": "大多数子宫癌发生在绝经后，中位确诊年龄为60岁。",
    "age.detail.liver": "肝癌发病高峰在55-64岁之间（SEER数据）。",
    "age.detail.thyroid": "甲状腺癌可发生于任何年龄，但最常见于30-50岁。",
    "age.detail.skin": "黑色素瘤风险随年龄和累积日晒暴露增加。",
    "age.detail.gastric": "胃癌风险在50岁后显著增加，大多数病例确诊于65-74岁。",
    "age.detail.testicular": "睾丸癌在15-35岁男性中最常见。",
    "age.detail.default": "在{{age}}岁时，定期筛查变得越来越重要。",

    // ─── RISK FACTOR TEMPLATES ───
    "risk.age.factor": "年龄 {{age}}岁",
    "risk.family.factor": "{{cancer}}家族史",
    "risk.family.detail": "一级亲属患有{{cancer}}会使风险增加约{{mult}}倍（NCI SEER数据）。",
    "risk.ethnicity.factor": "种族（{{ethnicity}}）",
    "risk.ethnicity.detail.higher": "{{ethnicity}}人群的{{cancer}}发病率较高（+{{pct}}%，SEER 2017-2021{{extra}}）。",
    "risk.ethnicity.detail.lower": "{{ethnicity}}人群的{{cancer}}发病率较低（{{pct}}%，SEER 2017-2021{{extra}}）。",
    "risk.ageContext.factor": "年龄因素说明",
    "risk.ageContext.skinYoungCaucasian": "虽然浅色皮肤确实会增加终生黑色素瘤风险，但这种风险主要随年龄和累积日晒而增长。在您这个年龄，重点做好防晒（涂防晒霜、避免晒伤）即可，不必急于筛查——除非您发现新出现或正在变化的痣。",
    "risk.bmi.factor": "BMI {{bmi}}（{{category}}）",
    "risk.bmi.detail": "BMI为{{bmi}}（{{category}}）与{{cancer}}风险增加{{pct}}%相关（WCRF/AICR; Chen等人2023年荟萃分析综述，PMID:37139871; Fontvieille等人2023年EPIC+UK Biobank研究，PMID:37993940）。",
    "risk.smoking.factor": "吸烟（{{packYears}}包年）",
    "risk.smoking.detail": "{{packYears}}包年的吸烟史使{{cancer}}风险增加约{{pct}}%（IARC, NCI; Zhang等人2022年UK Biobank 34.5万参与者研究：重度吸烟者肺癌HR 4.27，PMID:35194190; Wang等人2024年中国103万参与者研究：包年为首要风险因素，PMID:38253312）。",
    "risk.secondhandSmoke.factor": "二手烟暴露",
    "risk.secondhandSmoke.detail": "长期二手烟暴露使{{cancer}}风险增加约{{pct}}%（EPA, WHO）。",
    "risk.secondhand.factor": "二手烟暴露",
    "risk.secondhand.detail": "长期二手烟暴露使{{cancer}}风险增加约{{pct}}%（EPA, WHO）。",
    "risk.alcohol.factor": "饮酒（{{category}}）",
    "risk.alcohol.detail": "{{category}}饮酒使{{cancer}}风险增加约{{pct}}%（WCRF; Jun等人2023年139项队列研究荟萃分析，PMID:37905315——研究发现饮酒无安全剂量）。",
    "risk.smoking_alcohol.factor": "吸烟+饮酒交互作用",
    "risk.smoking_alcohol.detail": "吸烟和饮酒同时存在对{{cancer}}风险有乘法效应，额外增加约{{pct}}%的风险（IARC）。",
    "risk.radiation.factor": "辐射暴露",
    "risk.smokingAlcohol.factor": "吸烟与饮酒叠加效应",
    "risk.smokingAlcohol.detail": "吸烟与饮酒的联合作用对{{cancer}}风险具有叠加效应，额外增加约{{pct}}%的风险（IARC; Burton等人2024年，PMID:38000113）。",
    "risk.radiation.factor": "辐射暴露",
    "risk.radiation.detail": "辐射暴露使{{cancer}}风险增加约{{pct}}%（NCI, UNSCEAR）。",
    "risk.diet.factor": "不健康饮食习惯",
    "risk.chronic.factor": "慢性疾病：{{condition}}",
    "risk.chronic.detail": "患有慢性{{condition}}疾病使{{cancerKey}}癌风险增加约{{pct}}%（临床证据）。",
    "risk.diet.factor": "不健康饮食习惯",
    "risk.diet.detail": "高盐、加工肉类和饱和脂肪使{{cancer}}风险增加约{{pct}}%（WCRF/AICR）。",

    // ─── STATISTICS LABELS ───
    "stats.baseline": "人群基线终身风险",
    "stats.adjusted": "您的估算调整后风险",
    "stats.multiplier": "综合风险倍数",
    "stats.multiplier.value": "{{value}}倍基线",

    // ─── DESCRIPTION TEMPLATES ───
    "desc.noRisk": "根据您的年龄（{{age}}岁），建议进行常规{{cancer}}筛查作为预防措施。定期筛查有助于早期发现。",
    "desc.high": "根据您的情况——包括{{factors}}——您患{{cancer}}的风险可能高于平均水平。如果您有任何疑虑或异常症状，建议在下次就诊时与医生讨论筛查方案。",
    "desc.elevated": "您的{{factors}}组合可能使您患{{cancer}}的风险有所增加。如果您有任何疑虑或异常症状，建议在下次就诊时与医生讨论筛查方案。",
    "desc.default": "基于{{factors}}，您可能受益于{{cancer}}筛查。如果您有任何疑虑或异常症状，建议与医生讨论您的筛查选择。",

    // ─── GUIDELINE NOTES ───
    "guideline.cervical.under25": "指南建议女性从25岁开始进行宫颈筛查",
    "guideline.cervical.over65": "指南推荐的宫颈筛查适用至65岁",
    "guideline.breast.under30": "指南建议高风险个体从30岁开始进行乳腺筛查，一般风险人群从50岁开始",
    "guideline.breast.under50": "指南建议一般风险人群从50岁开始进行乳腺筛查，有家族史/BRCA基因突变者从30岁开始",
    "guideline.breast.over74": "政府资助的乳腺筛查适用至74岁",
    "guideline.colorectal.under40": "指南建议高风险个体从40岁开始进行结直肠筛查，一般风险人群从50岁开始",
    "guideline.colorectal.under50": "指南建议一般风险人群从50岁开始进行结直肠筛查，有家族史者从40岁开始",
    "guideline.colorectal.over74": "指南推荐的结直肠筛查适用至74岁",
    "guideline.lung.under50": "指南建议有≥20包年吸烟史者从50岁开始进行肺癌筛查",
    "guideline.lung.over80": "指南推荐的肺癌筛查适用至80岁",
    "guideline.lung.default": "指南建议50-80岁、吸烟≥20包年且目前仍在吸烟或戒烟不超过15年者进行肺癌筛查",

    // ─── ACADEMIC REFERENCE FINDINGS ───
    "ref.1.finding": "139项队列研究：饮酒无安全剂量；即使少量饮酒（0.01-12.4克/天）也显著增加食管癌、结直肠癌和乳腺癌风险。",
    "ref.2.finding": "每增加5 kg/m² BMI → 多种癌症风险增加1.01-1.13倍。肥胖与13种以上癌症相关，包括宫颈癌、甲状腺癌、结直肠癌、肝癌。",
    "ref.3.finding": "577,343名成人：每增加约5 kg/m²，肥胖相关癌症HR 1.11。肥胖与心血管疾病存在叠加交互作用（RERI 0.28）。",
    "ref.4.finding": "肥胖、男性、白种人、家族史和炎症性肠病是早发性结直肠癌的独立风险因素。",
    "ref.5.finding": "高度久坐行为使子宫内膜癌风险增加36%、结肠癌增加12%，以及肺癌风险增加。",
    "ref.6.finding": "UK Biobank 345,794名参与者：重度吸烟 → 肺癌HR 4.27；高遗传风险+重度吸烟 → HR 6.19。",
    "ref.7.finding": "代谢综合征增加消化道癌风险；肥胖与结直肠癌、胃癌和肝癌独立相关。",
    "ref.8.finding": "ALDH2缺陷的东亚人群面临显著更高的酒精相关癌症风险，肝癌和食管癌呈剂量-反应关系。",
    "ref.9.finding": "1,031,911名参与者：确定了7个个性化肺癌筛查风险因素，包括包年数、二手烟暴露和家族史。",
    "ref.10.finding": "非裔女性尽管吸烟率较低，但肺癌发病率更高，支持针对种族的风险调整。",

    // ─── HEALTH MANUAL — DIET ───
    "manual.diet.bmi.severe.title": "您的BMI为{{bmi}} — 严重肥胖（II-III级）",
    "manual.diet.bmi.severe.detail": "BMI≥35与13种以上癌症（包括乳腺癌、结直肠癌、子宫内膜癌、肝癌和肾癌）的风险显著升高相关。每增加5 kg/m²，总体癌症风险增加11%（Fontvieille等人2023年EPIC+UK Biobank研究）。即使体重减轻5-10%也能有效降低癌症生物标志物。",
    "manual.diet.bmi.severe.evidence": "Fontvieille et al., BMC Med 2023, PMID:37993940; NCI Obesity & Cancer 2022",
    "manual.diet.calorie.title": "目标每日减少500-750卡路里，逐步减重",
    "manual.diet.calorie.detail": "通过可持续的饮食调整实现每周减重0.5-1公斤——不要采用极端节食。建议咨询注册营养师制定结构化方案。地中海饮食和DASH饮食在癌症预防和体重管理方面证据最为充分。",
    "manual.diet.calorie.evidence": "WCRF/AICR 2018; Dominguez et al., Exp Gerontol 2023, PMID:36792040",
    "manual.diet.bmi.obese.title": "您的BMI为{{bmi}} — 肥胖（I级）",
    "manual.diet.bmi.obese.detail": "肥胖增加乳腺癌（绝经后，+30%）、结直肠癌（+30%）、子宫内膜癌（+150%）和肝癌（+80%）的风险。Chen等人2023年荟萃分析综述确认肥胖与13种以上癌症相关。即使适度减重也有帮助。",
    "manual.diet.bmi.obese.evidence": "Chen et al., Nutr Cancer 2023, PMID:37139871; WCRF/AICR 2018",
    "manual.diet.bmi.overweight.title": "您的BMI为{{bmi}} — 超重",
    "manual.diet.bmi.overweight.detail": "超重（BMI 25-29.9）会适度增加多种癌症风险。目标是防止体重继续增加，通过均衡饮食和规律运动达到健康BMI。",
    "manual.diet.bmi.overweight.evidence": "WCRF/AICR 2018",
    "manual.diet.bmi.underweight.title": "您的BMI为{{bmi}} — 体重偏低",
    "manual.diet.bmi.underweight.detail": "体重偏低（BMI<18.5）可能削弱免疫功能，并与食管癌和肺癌风险增加相关。建议摄入营养密度高的食物以达到健康体重。",
    "manual.diet.bmi.underweight.evidence": "Chen et al., Nutr Cancer 2023, PMID:37139871",
    "manual.diet.bmi.healthy.title": "您的BMI为{{bmi}} — 健康体重 ✓",
    "manual.diet.bmi.healthy.detail": "保持健康的BMI（18.5-24.9）是预防癌症最有效的措施之一。请通过均衡营养和规律运动继续保持。",
    "manual.diet.bmi.healthy.evidence": "WCRF/AICR 2018",
    "manual.diet.unhealthy.title": "您的饮食需要关注——高盐/加工食品摄入",
    "manual.diet.unhealthy.detail": "根据您的回答，您的饮食模式与癌症风险增加相关。高盐饮食损伤胃黏膜并促进胃癌发生。加工肉类是IARC 1类致癌物——每天50克加工肉类使结直肠癌风险增加18%。饱和脂肪促进炎症。",
    "manual.diet.unhealthy.evidence": "IARC Monograph Vol. 114; WCRF/AICR CUP; Bouvard et al., Lancet Oncol 2015",
    "manual.diet.actionable.title": "可操作的饮食调整",
    "manual.diet.actionable.detail": "1) 红肉限制在每周3份以内，避免加工肉类（培根、香肠、午餐肉）。2) 减少钠摄入至每天<2,300毫克——查看食品标签，自己烹饪。3) 用橄榄油、坚果、牛油果替代饱和脂肪。4) 通过全谷物、豆类、蔬菜增加每天25-30克膳食纤维。",
    "manual.diet.actionable.evidence": "ACS Nutrition Guidelines 2024; WCRF/AICR 2018",
    "manual.diet.good.title": "您的饮食习惯良好——继续优化",
    "manual.diet.good.detail": "您没有报告明显不健康的饮食模式。为进一步降低癌症风险：每天5份以上水果蔬菜（400克+），多吃十字花科蔬菜（西兰花、羽衣甘蓝——含萝卜硫素），选择全谷物代替精制谷物。",
    "manual.diet.good.evidence": "Aune et al., Int J Epidemiol 2017, PMID:28338764 (142 publications)",

    // ─── HEALTH MANUAL — LIFESTYLE ───
    "manual.lifestyle.active_smoker.title": "现在吸烟——{{packYears}}包年——这是您的首要问题",
    "manual.lifestyle.active_smoker.detail": "烟草是最大的可预防癌症死亡原因。{{packYears}}包年的吸烟史使您的肺癌风险显著升高（与从不吸烟者相比HR {{hr}}，Zhang等人2022年UK Biobank研究）。任何年龄戒烟都有益——戒烟10年内肺癌风险降低约50%。",
    "manual.lifestyle.active_smoker.detail_light": "任何程度的吸烟都会增加癌症风险。{{packYears}}包年的暴露量还比较低（与从不吸烟者相比HR {{hr}}），现在戒烟意味着您的风险可以在几年内恢复到接近基线水平。戒烟的最佳时机就是今天。",
    "manual.lifestyle.active_smoker.evidence": "Zhang et al., Br J Cancer 2022, PMID:35194190; US Surgeon General 2014",
    "manual.lifestyle.cessation.title": "循证戒烟方案",
    "manual.lifestyle.cessation.detail": "联合疗法成功率最高：1) 尼古丁替代疗法（贴片+口香糖/含片），2) 伐尼克兰（畅沛）——最有效的单药，3) 安非他酮（威博隽）。药物联合咨询可使戒烟成功率翻倍。",
    "manual.lifestyle.cessation.evidence": "CDC Clinical Practice Guideline for Treating Tobacco Use 2020; Cahill et al., Cochrane 2016",
    "manual.lifestyle.former_smoker.title": "已戒烟（{{packYears}}包年）——您的风险正在降低",
    "manual.lifestyle.former_smoker.detail": "戒烟是明智的决定。您的癌症风险正在持续下降。戒烟5年后，口腔/咽喉/食管/膀胱癌风险降低一半。10年后肺癌风险降低约50%。15年后冠心病风险接近从未吸烟者。",
    "manual.lifestyle.former_smoker.evidence": "US Surgeon General 2020; ACS",
    "manual.lifestyle.ldct_eligible.title": "您可能符合肺癌筛查（LDCT）条件",
    "manual.lifestyle.ldct_eligible.detail": "您有{{packYears}}包年吸烟史，如果您在过去15年内戒烟且年龄在50-80岁之间，可能符合每年低剂量CT筛查条件。LDCT可降低20%的肺癌死亡率。",
    "manual.lifestyle.ldct_eligible.evidence": "USPSTF 2021 (Grade B); National Lung Screening Trial",
    "manual.lifestyle.secondhand.title": "二手烟暴露——采取保护措施",
    "manual.lifestyle.secondhand.detail": "与吸烟者共同生活使您的肺癌风险增加20-30%。二手烟每年导致美国约7,300名非吸烟者死于肺癌。让您的家成为无烟环境，并支持家人的戒烟努力。",
    "manual.lifestyle.secondhand.evidence": "EPA 1992; US Surgeon General 2006; Wang et al., Chest 2024, PMID:38253312",
    "manual.lifestyle.heavy_alcohol.title": "大量饮酒——显著癌症风险因素",
    "manual.lifestyle.heavy_alcohol.detail": "您报告的饮酒量属于{{category}}类别。2023年一项涵盖139项队列研究的荟萃分析发现，重度饮酒（≥50克/天）显著增加几乎所有类型癌症的风险。即使少量饮酒也会增加乳腺癌、结直肠癌和食管癌风险。就癌症预防而言，饮酒无安全剂量。",
    "manual.lifestyle.heavy_alcohol.detail.asian": "注意：东亚人群中ALDH2缺陷（常见变异）者面临更高的酒精相关癌症风险。",
    "manual.lifestyle.heavy_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315; IARC Group 1 carcinogen",
    "manual.lifestyle.alcohol_reduction.title": "减酒策略",
    "manual.lifestyle.alcohol_reduction.detail": "从每周安排无酒日开始（目标3-4天/周）。社交前设定饮酒上限。选择低酒精饮品。用日记记录饮酒量。如果无法自行减少，医生可以帮助——纳曲酮等药物可减少酒精渴望。",
    "manual.lifestyle.alcohol_reduction.evidence": "ACS Alcohol & Cancer Guidelines; NIAAA",
    "manual.lifestyle.moderate_alcohol.title": "适度饮酒——建议为预防癌症进一步减少",
    "manual.lifestyle.moderate_alcohol.detail": "即使适度饮酒（每天1-2杯）也会使乳腺癌风险增加约10%，结直肠癌风险增加约10%。ACS建议女性每天≤1杯，男性≤2杯——但承认就癌症而言，越少越好。",
    "manual.lifestyle.moderate_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315; ACS Guidelines",
    "manual.lifestyle.light_alcohol.title": "少量饮酒——低但非零的癌症风险",
    "manual.lifestyle.light_alcohol.detail": "即使少量饮酒也会略微增加乳腺癌和食管癌风险。您目前的摄入量在通常可接受的范围内，但请注意就癌症而言「无安全剂量」。",
    "manual.lifestyle.light_alcohol.evidence": "Jun et al., Epidemiol Health 2023, PMID:37905315",
    "manual.lifestyle.none.title": "未发现主要生活方式风险因素 ✓",
    "manual.lifestyle.none.detail": "您不吸烟且饮酒极少或不饮酒。这是两个最重要的癌症预防因素。请保持这些习惯——它们对您的保护作用比您想象的要大。",
    "manual.lifestyle.none.evidence": "ACS Cancer Prevention Guidelines 2024",

    // ─── HEALTH MANUAL — EXERCISE ───
    "manual.exercise.obese.title": "从每周150分钟开始，逐步增加至300分钟",
    "manual.exercise.obese.detail": "鉴于您的BMI，运动的癌症预防效益尤为显著。体力活动可降低结肠癌风险20-25%、乳腺癌20%和子宫内膜癌20%。在体重管理方面，ACS建议逐步增加至每周300分钟中等强度活动。从您舒适的运动量开始，逐步增加。",
    "manual.exercise.obese.evidence": "WCRF/AICR 2018; Moore et al., JAMA Intern Med 2016, PMID:27183032 (1.44M participants)",
    "manual.exercise.normal.title": "目标每周150分钟以上中等强度活动",
    "manual.exercise.normal.detail": "规律体力活动是最有效的可改变癌症预防因素之一。它通过改善胰岛素敏感性、减少炎症和增强免疫调节来降低结肠癌（20-25%）、乳腺癌（20%）、子宫内膜癌（20%）及其他多种癌症风险。",
    "manual.exercise.normal.evidence": "WCRF/AICR 2018 (convincing evidence); Moore et al., JAMA Intern Med 2016, PMID:27183032",
    "manual.exercise.strength.title": "每周增加2天以上力量训练",
    "manual.exercise.strength.detail": "抗阻运动可改善胰岛素敏感性、降低炎症标志物（IL-6、TNF-α）并维持瘦体重——这些都是抗癌保护机制。荟萃分析发现肌肉强化活动与癌症死亡率降低13%相关。",
    "manual.exercise.strength.detail.cardio": "注意：鉴于您的心血管病史，请从轻度阻力训练开始，并咨询医生确定安全的运动强度。",
    "manual.exercise.strength.evidence": "Momma et al., Br J Sports Med 2022, PMID:35228201; WHO 2020",
    "manual.exercise.sitting.title": "每30-60分钟打断久坐",
    "manual.exercise.sitting.detail": "久坐行为（每天>8小时）是独立的癌症风险因素。久坐时间使子宫内膜癌风险增加36%、结肠癌增加12%，即使在经常运动的人群中也是如此（Hermelink等人2022年研究）。设置每小时活动提醒。",
    "manual.exercise.sitting.evidence": "Hermelink et al., Eur J Epidemiol 2022, PMID:35612669",

    // ─── HEALTH MANUAL — VITAMINS ───
    "manual.vitamins.vitd.title": "维生素D——先检测，再决定是否补充",
    "manual.vitamins.vitd.detail": "低维生素D（<20 ng/mL）与结直肠癌、乳腺癌和前列腺癌风险增加相关。VITAL试验（n=25,871）发现维生素D补充在长期随访中使癌症死亡率降低25%。请让医生检测25(OH)D血清水平。",
    "manual.vitamins.vitd.evidence": "Manson et al., NEJM 2019;380:33-44 (VITAL trial); IOM 2011",
    "manual.vitamins.calcium.title": "钙 1,000-1,200毫克/天用于骨骼健康",
    "manual.vitamins.calcium.detail": "50岁后充足的钙摄入对骨骼健康很重要。食物来源（乳制品、强化食品、深绿色蔬菜）优于补充剂。过量补充（>1,500毫克/天）可能增加心血管风险。",
    "manual.vitamins.calcium.evidence": "IOM 2011; USPSTF 2018",
    "manual.vitamins.folate.title": "叶酸和铁——育龄女性必需",
    "manual.vitamins.folate.detail": "建议所有育龄女性每天补充叶酸400-800微克以预防神经管缺陷。如果月经量大或很少吃肉，请检查铁蛋白水平。优先从食物中获取营养。",
    "manual.vitamins.folate.evidence": "USPSTF 2023 (Grade A for folic acid); WHO Iron Guidelines",
    "manual.vitamins.antioxidant.title": "不建议服用抗氧化剂补充剂预防癌症",
    "manual.vitamins.antioxidant.detail": "多项大型试验表明抗氧化剂补充剂（β-胡萝卜素、维生素E、硒）不能降低癌症风险，反而可能增加风险。SELECT试验发现维生素E使前列腺癌风险增加17%。β-胡萝卜素使吸烟者肺癌风险增加18%。请从天然食物中获取营养。",
    "manual.vitamins.antioxidant.evidence": "Klein et al., JAMA 2011 (SELECT); ATBC Study, NEJM 1994; USPSTF 2022 (Grade I)",

    // ─── HEALTH MANUAL — SCREENING ───
    "manual.screening.colorectal.title": "结直肠癌筛查——现在应该开始",
    "manual.screening.colorectal.detail.age": "在{{age}}岁时，您应该积极进行结直肠癌筛查（USPSTF A级推荐，45-75岁）。",
    "manual.screening.colorectal.detail.family": "鉴于您有结直肠癌家族史，筛查应从40岁开始或在亲属确诊年龄前10年开始。",
    "manual.screening.colorectal.detail.options": "可选方案：结肠镜每10年一次、每年FIT（粪便检测）或FIT-DNA每1-3年一次。",
    "manual.screening.colorectal.evidence": "USPSTF 2021 (Grade A, 45-75)",
    "manual.screening.breast.title": "乳腺癌筛查——建议进行钼靶检查",
    "manual.screening.breast.detail": "在{{age}}岁时，USPSTF建议每两年进行一次钼靶检查（2024年更新为从40岁开始，此前为50岁）。",
    "manual.screening.breast.detail.family": "鉴于您的家族史，请与医生讨论是否需要每年钼靶检查或增加乳腺MRI。",
    "manual.screening.breast.detail.nofamily": "ACS建议每年进行钼靶检查。",
    "manual.screening.breast.evidence": "USPSTF 2024 (Grade B, biennial 40-74); ACS 2015",
    "manual.screening.cervical.title": "宫颈癌筛查——Pap + HPV检测",
    "manual.screening.cervical.detail": "21-29岁：每3年Pap涂片检查。30-65岁：每5年Pap+HPV联合检测（首选）。",
    "manual.screening.cervical.detail.hpv": "HPV疫苗仍可接种（批准至45岁），可预防约90%的HPV相关癌症。",
    "manual.screening.cervical.evidence": "USPSTF 2018 (Grade A); CDC HPV Vaccination 2024",
    "manual.screening.prostate.title": "前列腺癌——建议尽早咨询",
    "manual.screening.prostate.detail.african": "作为非裔男性，您的前列腺癌发病率高出1.7倍。PCF建议从40岁开始讨论筛查。",
    "manual.screening.prostate.detail.family": "鉴于您的家族史，前列腺筛查讨论应早于一般建议的55岁。",
    "manual.screening.prostate.detail.suffix": "PSA检测是一个共同决策——请与医生讨论利弊。",
    "manual.screening.prostate.evidence": "USPSTF 2018 (Grade C, 55-69); PCF Black Men Guidelines 2023",
    "manual.screening.liver.title": "乙肝/丙肝筛查——对预防肝癌很重要",
    "manual.screening.liver.detail.asian": "作为亚裔，您面临更高的乙肝风险（亚裔人群HBV感染率较高）。",
    "manual.screening.liver.detail": "慢性HBV/HCV导致约80%的肝癌。请同时检测两者——丙肝现在治愈率>95%。如果您没有免疫力，可以接种乙肝疫苗。",
    "manual.screening.liver.evidence": "USPSTF HCV 2020 (Grade B); AASLD 2023",
    "manual.screening.age_appropriate.title": "需要关注的年龄相关筛查",
    "manual.screening.age_appropriate.detail": "在{{age}}岁时，请记住这些重要时间节点：女性21岁开始宫颈筛查，45岁开始结直肠癌筛查，女性40岁开始乳腺钼靶检查，男性55岁开始讨论前列腺筛查（非裔或有家族史者更早）。请定期体检。",
    "manual.screening.age_appropriate.evidence": "USPSTF A/B Recommendations 2024",

    // ─── HEALTH MANUAL — PREVENTION ───
    "manual.prevention.diabetes.title": "管理糖尿病就是预防癌症",
    "manual.prevention.diabetes.detail": "2型糖尿病增加结直肠癌（+30%）、肝癌（+100%）、胰腺癌、乳腺癌和膀胱癌的风险（Zhan等人2024年）。最佳血糖控制（HbA1c<7%）可降低这些风险。二甲双胍可能具有额外的抗癌保护作用（正在研究中）。",
    "manual.prevention.diabetes.evidence": "Zhan et al., J Gastroenterol Hepatol 2024, PMID:38230882; NCI",
    "manual.prevention.cvd.title": "心血管疾病与癌症共享风险因素",
    "manual.prevention.cvd.detail": "心血管疾病和癌症共享共同的风险因素：肥胖、缺乏运动、吸烟、不良饮食、炎症。2023年EPIC/UK Biobank研究（57.7万成人）发现肥胖与心血管疾病在癌症风险方面存在叠加交互作用。管理心血管风险因素同时也降低了您的癌症风险。",
    "manual.prevention.cvd.evidence": "Fontvieille et al., BMC Med 2023, PMID:37993940",
    "manual.prevention.sleep.title": "保证每晚7-9小时规律睡眠",
    "manual.prevention.sleep.detail": "IARC将夜班工作/昼夜节律紊乱列为可能致癌物（2A类）。睡眠不足会抑制褪黑素（具有抗癌特性）并损害DNA修复能力。即使在周末也要保持一致的作息时间。",
    "manual.prevention.sleep.evidence": "IARC Monograph Vol. 124 (2019); Rivera et al., Int J Cancer 2020",
    "manual.prevention.uv.title": "紫外线防护——每天在暴露皮肤上使用SPF 30+防晒霜",
    "manual.prevention.uv.detail": "使用广谱SPF 30+防晒霜，户外每2小时补涂一次。完全避免使用日光浴床。自1975年以来黑色素瘤发病率增加了320%。",
    "manual.prevention.uv.detail.caucasian": "白种人皮肤的黑色素瘤风险显著更高——建议每年进行皮肤科检查。",
    "manual.prevention.uv.evidence": "AAD; Green et al., J Clin Oncol 2011 (sunscreen reduces melanoma by 50%)",

    // ─── HEALTH SCREENING RECOMMENDATIONS (cardiometabolic) ───
    "hs.bloodSugar.category": "血糖与糖尿病",
    "hs.cholesterol.category": "胆固醇",
    "hs.bloodPressure.category": "血压",

    "hs.diabetes.diagnosed": "已确诊糖尿病",
    "hs.diabetes.prediabetic": "血糖处于糖尿病前期水平",
    "hs.diabetes.age45": "45岁以上",
    "hs.diabetes.overweight": "超重/肥胖（BMI ≥ 25）",
    "hs.diabetes.ethnicity": "高风险种族（亚裔人群建议BMI ≥ 23时即进行筛查）",
    "hs.diabetes.family": "糖尿病家族史",
    "hs.diabetes.hypertension": "高血压",
    "hs.diabetes.cholesterol": "高胆固醇",

    "hs.diabetes.screening.method": "空腹血糖（FPG）或糖化血红蛋白（HbA1c）血液检测",
    "hs.diabetes.screening.guideline": "USPSTF B级推荐；ADA 2024年诊疗标准",
    "hs.diabetes.screening.who": "35-70岁超重/肥胖成人；有风险因素者应更早筛查",
    "hs.diabetes.screening.interval.diagnosed": "每3个月检测HbA1c；每年全面代谢检查",
    "hs.diabetes.screening.interval.high": "每6-12个月复查；考虑口服葡萄糖耐量试验（OGTT）",
    "hs.diabetes.screening.interval.default": "正常者每3年一次；有风险因素者每年一次",
    "hs.diabetes.screening.source": "USPSTF 2021; ADA 2024; CDC",

    "hs.diabetes.action.diagnosed.1": "按医生指导每日监测血糖",
    "hs.diabetes.action.diagnosed.2": "将HbA1c控制在7%以下（或医生设定的目标）",
    "hs.diabetes.action.diagnosed.3": "每年进行眼科检查、足部检查和肾功能检测",
    "hs.diabetes.action.diagnosed.4": "低精制碳水化合物和添加糖的心脏健康饮食",
    "hs.diabetes.action.diagnosed.5": "每周150分钟以上中等强度体力活动",
    "hs.diabetes.action.high.1": "减少精制碳水化合物和糖的摄入",
    "hs.diabetes.action.high.2": "如超重，目标减重体重的5-7%",
    "hs.diabetes.action.high.3": "每周150分钟以上中等强度运动（快走、骑车）",
    "hs.diabetes.action.high.4": "咨询医生是否使用二甲双胍预防糖尿病",
    "hs.diabetes.action.high.5": "6-12个月后复查空腹血糖或HbA1c",
    "hs.diabetes.action.default.1": "保持富含全谷物、蔬菜和优质蛋白的均衡饮食",
    "hs.diabetes.action.default.2": "保持运动——每周150分钟以上中等强度运动",
    "hs.diabetes.action.default.3": "了解自己的数值：下次体检时做空腹血糖检测",
    "hs.diabetes.action.default.4": "限制含糖饮料和加工食品",

    "hs.cholesterol.high": "高胆固醇（≥ 240 mg/dL 或已确诊）",
    "hs.cholesterol.borderline": "临界高胆固醇（200–239 mg/dL）",
    "hs.cholesterol.age40": "40岁以上",
    "hs.cholesterol.smoking": "吸烟史",
    "hs.cholesterol.diabetes": "糖尿病",
    "hs.cholesterol.hypertension": "高血压",
    "hs.cholesterol.overweight": "超重/肥胖",
    "hs.cholesterol.male45": "45岁以上男性",
    "hs.cholesterol.female55": "55岁以上女性",

    "hs.cholesterol.screening.method": "血脂检查（总胆固醇、LDL、HDL、甘油三酯）",
    "hs.cholesterol.screening.guideline": "ACC/AHA 2019; USPSTF B级推荐（40-75岁成人）",
    "hs.cholesterol.screening.who": "20岁以上成人（基线）；40-75岁或有风险因素者定期检查",
    "hs.cholesterol.screening.interval.high": "每6-12个月，监测治疗效果",
    "hs.cholesterol.screening.interval.moderate": "每年一次",
    "hs.cholesterol.screening.interval.default": "正常者每4-6年一次；有风险因素者更频繁",
    "hs.cholesterol.screening.source": "ACC/AHA 2019; USPSTF 2023",

    "hs.cholesterol.action.high.1": "如尚未服用他汀类药物，请与医生讨论",
    "hs.cholesterol.action.high.2": "减少饱和脂肪摄入（红肉、全脂乳制品、油炸食品）",
    "hs.cholesterol.action.high.3": "增加可溶性纤维（燕麦、豆类、水果、蔬菜）",
    "hs.cholesterol.action.high.4": "每周至少运动150分钟——即使是散步也有帮助",
    "hs.cholesterol.action.high.5": "考虑omega-3补充剂或鱼油（请咨询医生）",
    "hs.cholesterol.action.moderate.1": "采用心脏健康饮食（地中海或DASH饮食）",
    "hs.cholesterol.action.moderate.2": "增加体力活动——目标每天30分钟",
    "hs.cholesterol.action.moderate.3": "限制饮酒至适度水平",
    "hs.cholesterol.action.moderate.4": "6-12个月后复查血脂",
    "hs.cholesterol.action.default.1": "保持心脏健康饮食，多吃健康脂肪（橄榄油、坚果、牛油果）",
    "hs.cholesterol.action.default.2": "保持运动——规律运动可提高HDL（好胆固醇）",
    "hs.cholesterol.action.default.3": "如果没做过血脂检查，建议做基线检查",
    "hs.cholesterol.action.default.4": "限制反式脂肪和高度加工食品",

    "hs.bp.high": "高血压（≥ 140/90 mmHg 或已确诊）",
    "hs.bp.elevated": "血压偏高（120–139/80–89 mmHg）",
    "hs.bp.age45": "45岁以上",
    "hs.bp.african": "非裔美国人（发病率较高）",
    "hs.bp.overweight": "超重/肥胖",
    "hs.bp.smoking": "吸烟史",
    "hs.bp.diabetes": "糖尿病",
    "hs.bp.cholesterol": "高胆固醇",
    "hs.bp.alcohol": "中度至重度饮酒",

    "hs.bp.screening.method": "血压测量（血压计或电子血压计）",
    "hs.bp.screening.guideline": "AHA/ACC 2023; USPSTF A级推荐（18岁以上成人）",
    "hs.bp.screening.who": "所有18岁以上成人；有风险因素者更频繁",
    "hs.bp.screening.interval.high": "在家定期监测；每1-3个月就诊直至血压得到控制",
    "hs.bp.screening.interval.moderate": "每3-6个月检查；考虑家庭监测",
    "hs.bp.screening.interval.default": "至少在常规体检时每年测量一次",
    "hs.bp.screening.source": "AHA 2023; USPSTF 2021",

    "hs.bp.action.high.1": "按医嘱服药——不要漏服",
    "hs.bp.action.high.2": "将钠摄入量减少到每天<2,300毫克（理想<1,500毫克）",
    "hs.bp.action.high.3": "遵循DASH饮食（水果、蔬菜、全谷物、低脂乳制品）",
    "hs.bp.action.high.4": "规律运动——每天30分钟快走就有帮助",
    "hs.bp.action.high.5": "在家监测血压并为医生记录数据",
    "hs.bp.action.high.6": "通过冥想、深呼吸或瑜伽管理压力",
    "hs.bp.action.moderate.1": "减少钠摄入——避免加工食品和外出就餐",
    "hs.bp.action.moderate.2": "增加富含钾的食物（香蕉、菠菜、红薯）",
    "hs.bp.action.moderate.3": "每周至少150分钟有氧运动",
    "hs.bp.action.moderate.4": "限制饮酒：女性每天≤1杯，男性每天≤2杯",
    "hs.bp.action.moderate.5": "保持健康体重——即使减掉2-5公斤也有帮助",
    "hs.bp.action.default.1": "保持适度钠摄入（每天<2,300毫克）",
    "hs.bp.action.default.2": "保持规律体力活动",
    "hs.bp.action.default.3": "下次就医时检查血压",
    "hs.bp.action.default.4": "保持健康体重并管理压力",

    // ─── BMI CATEGORY NAMES ───
    "bmi.underweight": "underweight",
    "bmi.normal": "normal",
    "bmi.overweight": "overweight",
    "bmi.obese": "obese",
    "bmi.severeObese": "severe obesity",

    // ─── ALCOHOL CATEGORY NAMES ───
    "alcohol.light": "Light",
    "alcohol.moderate": "Moderate",
    "alcohol.heavy": "Heavy",
    "alcohol.veryHeavy": "Very heavy",
  },
};

// Make zh bmi/alcohol categories Chinese
T.zh["bmi.underweight"] = "偏瘦";
T.zh["bmi.normal"] = "正常";
T.zh["bmi.overweight"] = "超重";
T.zh["bmi.obese"] = "肥胖";
T.zh["bmi.severeObese"] = "严重肥胖";
T.zh["alcohol.light"] = "少量";
T.zh["alcohol.moderate"] = "适量";
T.zh["alcohol.heavy"] = "大量";
T.zh["alcohol.veryHeavy"] = "大量";

/**
 * Translate a key with optional parameter interpolation.
 * @param {string} locale - "en" or "zh"
 * @param {string} key - translation key
 * @param {Object} params - interpolation params, e.g. { age: 45 }
 * @returns {string}
 */
export function t(locale, key, params = {}) {
  const lang = T[locale] ? locale : "en";
  const str = T[lang]?.[key] ?? T.en?.[key] ?? key;
  if (!params || Object.keys(params).length === 0) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (match, k) => (params[k] !== undefined ? params[k] : match));
}

export default T;
